<template>
  <div id="Shippers">
    <BreadCrumb :items="breadcrumbItems" />

    <v-card elevation="1" class="pt-5 mt-1">
      <div class="header d-flex justify-space-between pr-5 align-center">
        <h1>Lista de embarcadores</h1>
        <router-link to="/shippers/new">
          <v-btn elevation="2" color="success" large>NOVO EMBARCADOR</v-btn>
        </router-link>
      </div>

      <div class="table mt-10">
        <v-data-table
          :headers="headers"
          :items="shippers"
          :loading="isLoading"
          :items-per-page="pageSize"
          @update:items-per-page="onItemsPerPageChange"
          :footer-props="{
            'items-per-page-options': [10, 20, 30, 50],
            itemsPerPageText: 'Itens por página'
          }"
        >
          <template v-slot:[`item.isActive`]="{ item, index }">
            <v-switch
              :input-value="item.isActive"
              color="success"
              inset
              readonly
              @click.stop.prevent="onChangeStatusClick(item, index)"
            ></v-switch>
          </template>
          <template v-slot:[`item.isPhysicalPerson`]="{ item }">
            <v-chip v-if="item.isPhysicalPerson" color="info"> Pessoal </v-chip>
            <v-chip v-else color="success"> Empresa </v-chip>
          </template>
          <template v-slot:[`item.cpfOrCnpjs`]="{ item }">
            <TruncateDataWithTooltip
              :data="truncateCpfCpnj(item.cpfOrCnpjs, item.isPhysicalPerson)"
              :lengthDataToTruncate="20"
            ></TruncateDataWithTooltip>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex align-center" style="gap: 10px">
              <router-link class="d-flex align-center" :to="`/shippers/${item.id}`">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <PencilSVG v-bind="attrs" v-on="on" />
                  </template>
                  <span>Editar</span>
                </v-tooltip>
              </router-link>
              <router-link class="d-flex align-center" :to="`/shippers/${item.id}/details`">
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <ViewSVG v-bind="attrs" v-on="on" />
                  </template>
                  <span>Detalhes</span>
                </v-tooltip>
              </router-link>
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <div @click="onDeleteClick(item)" class="d-flex align-center">
                    <TrashSVG role="button" title="Deletar" v-bind="attrs" v-on="on" />
                  </div>
                </template>
                <span>Deletar</span>
              </v-tooltip>
            </div>
          </template>
        </v-data-table>
        <v-pagination v-model="pageNumber" :length="totalPages" :total-visible="7" @input="onPageChange"></v-pagination>
      </div>
    </v-card>
    <confirm-dialog
      :isOpen.sync="isChangeStatusDialogOpen"
      @confirmClick="handleChangeStatus"
      :isConfirmBtnLoading="isChangeStatusButtonLoading"
      title="Mudar status do embarcador "
      :body-text="changeStatusBodyText"
    />
    <confirm-dialog
      :isOpen.sync="isDeleteConfirmDialogOpen"
      @confirmClick="handleDelete"
      :isConfirmBtnLoading="isDeleteButtonLoading"
      title="Deletar embarcador?"
      :body-text="deleteBodyText"
    />
    <MessagesError :errors="errors" :type="errorsType" @closeMessages="errors = []" />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import BreadCrumb from '@/components/BreadCrumb.vue';
import ConfirmDialog from '@/components/ConfirmDialog';
import PencilSVG from '@/assets/img/pencil.svg';
import TrashSVG from '@/assets/img/trash.svg';
import ViewSVG from '@/assets/img/view.svg';
import MessagesError from '@/components/MessagesError.vue';
import TruncateDataWithTooltip from '@/components/Table/TruncateDataWithTooltip.vue';
import maskCpf from '@/utils/maskCPF';
import maskCnpj from '@/utils/maskCNPJ';
export default {
  name: 'Shippers',
  components: {
    BreadCrumb,
    ConfirmDialog,
    PencilSVG,
    TrashSVG,
    ViewSVG,
    MessagesError,
    TruncateDataWithTooltip
  },
  data: () => ({
    errors: [],
    errorsType: 'error',
    selectedShipper: null,
    changeStatusBodyText: '',
    deleteBodyText: '',
    isDeleteConfirmDialogOpen: false,
    isChangeStatusDialogOpen: false,
    isChangeStatusButtonLoading: false,
    isDeleteButtonLoading: false,
    breadcrumbItems: [
      {
        text: 'Início',
        disabled: false,
        to: '/start'
      },
      {
        text: 'Embarcadores',
        disabled: true,
        to: '/shippers'
      }
    ],
    headers: [
      { text: 'NOME', value: 'name', align: 'start' },
      { text: 'NOME FANTASIA', value: 'fantasyName' },
      { text: 'CNPJ/CPF', value: 'cpfOrCnpjs' },
      { text: 'TIPO DE EMBARCADOR', value: 'isPhysicalPerson' },
      { text: 'STATUS', value: 'isActive' },
      { text: 'AÇÕES', value: 'actions' }
    ],
    shippers: [],
    isLoading: false,
    totalPages: 1,
    pageNumber: 1,
    pageSize: 10
  }),

  async created() {
    this.getAllShippers();
  },

  methods: {
    ...mapActions('ShippersStore', ['getShippers', 'disableShipper', 'enableShipper', 'deleteShipper']),

    async getAllShippers() {
      this.isLoading = true;
      try {
        const pagination = { pageNumber: this.pageNumber, pageSize: this.pageSize };
        const { data, totalPages } = await this.getShippers(pagination);

        this.shippers = data;
        this.totalPages = totalPages;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    onChangeStatusClick(item) {
      this.selectedShipper = item;
      this.changeStatusBodyText = `Você confirma estar mudando o status do ${this.selectedShipper.name}?`;
      this.isChangeStatusDialogOpen = true;
    },

    onItemsPerPageChange(pageSize) {
      this.pageSize = pageSize;
      this.getAllShippers();
    },

    onPageChange(page) {
      this.pageNumber = page;
      this.getAllShippers();
    },

    onDeleteClick(item) {
      this.isDeleteConfirmDialogOpen = true;
      this.selectedShipper = item;
      this.deleteBodyText = `Você confirma estar deletando o embarcador ${this.selectedShipper.name}?`;
    },

    async handleDelete() {
      try {
        this.isDeleteButtonLoading = true;
        const status = await this.deleteShipper(this.selectedShipper.id);
        if (status.success) {
          this.handleFeedback('success', ['Embarcador deletado com sucesso!']);
          this.isDeleteConfirmDialogOpen = false;
          this.getAllShippers();
        }
      } catch (error) {
        this.handleFeedback('error', [error]);
      } finally {
        this.isDeleteButtonLoading = false;
      }
    },

    async handleChangeStatus() {
      const { isActive } = this.selectedShipper;
      this.isChangeStatusButtonLoading = true;
      try {
        if (isActive) {
          const status = await this.disableShipper(this.selectedShipper.id);
          if (status.success) {
            this.handleFeedback('success', ['Embarcador desabilitado com sucesso!']);
            this.isChangeStatusDialogOpen = false;
            this.getAllShippers();
          }
        } else {
          const status = await this.enableShipper(this.selectedShipper.id);
          if (status.success) {
            this.handleFeedback('success', ['Embarcador habilitado com sucesso!']);
            this.isChangeStatusDialogOpen = false;
            this.getAllShippers();
          }
        }
      } catch (error) {
        this.handleFeedback('error', [error]);
      } finally {
        this.isChangeStatusButtonLoading = false;
      }
    },

    handleFeedback(errorType, errors) {
      this.errorsType = errorType;
      this.errors = errors;
    },
    truncateCpfCpnj(array, isPhysicalPerson) {
      if (isPhysicalPerson) {
        array = array.map((item) => maskCpf(item));
        return array.join(', ');
      }
      array = array.map((item) => maskCnpj(item));
      return array.join(', ');
    }
  }
};
</script>

<style></style>
